import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Sell Property", item: "https://cashmyproperty.ai/services/sell-property" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How long does it take to sell my property through CashMyProperty?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most properties sell within 45–90 days. Our AI pre-qualifies buyers before they ever see your listing, which dramatically shortens the sales cycle. High-demand plots in prime locations have closed in under 30 days. We provide a realistic timeline estimate in your free property audit.",
      },
    },
    {
      "@type": "Question",
      name: "What commission or fees do you charge?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Our success fee is 1–2% of the transaction value, charged only upon successful closure. There are no upfront listing fees, no marketing charges, and no hidden costs. The AI Feasibility Report at ₹4,999 is the only pre-sale investment — and it often helps you command a 15–25% higher price.",
      },
    },
    {
      "@type": "Question",
      name: "Do you list on regular property portals?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We go far beyond portals. Our approach combines AI-powered buyer matching from a verified database of 15,000+ active investors, targeted digital campaigns, institutional buyer outreach, and broker-network syndication. Your property gets exposure to serious buyers — not casual browsers.",
      },
    },
    {
      "@type": "Question",
      name: "What documents do I need to sell my property?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You will need the title deed (sale deed), encumbrance certificate (EC), latest tax receipts, Aadhaar/PAN cards of all owners, approved building/layout plan (if applicable), and khata certificate. Our legal team guides you through the complete documentation checklist specific to your property type.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "Free Property Audit",
    desc: "We conduct a thorough audit of your property — title status, encumbrance, zoning classification, market comparables, and development potential. Delivered as a detailed report within 48 hours.",
    icon: "ri-search-eye-line",
  },
  {
    step: "02",
    title: "AI-Powered Price Benchmarking",
    desc: "Our AI analyzes 50+ data points — recent sales, infrastructure projects, demand trends, and buyer profiles — to determine the optimal listing price that maximizes your return without stalling the sale.",
    icon: "ri-cpu-line",
  },
  {
    step: "03",
    title: "Verified Buyer Matching",
    desc: "We match your property against our database of 15,000+ pre-verified buyers — HNIs, NRIs, institutional funds, and end-users actively seeking land in your area. Only serious, financially qualified prospects.",
    icon: "ri-user-star-line",
  },
  {
    step: "04",
    title: "Multi-Channel Marketing",
    desc: "Your property gets exposure through targeted digital campaigns, our broker network of 800+ agents, institutional investor outreach, and direct mail to matched buyer segments. Maximum reach, minimum noise.",
    icon: "ri-megaphone-line",
  },
  {
    step: "05",
    title: "Negotiation & Closure",
    desc: "Our transaction team manages offer evaluation, counter-negotiation, legal due diligence, and registration. We stay with you until the sale deed is registered and funds are in your account.",
    icon: "ri-hand-coin-line",
  },
];

const benefits = [
  { icon: "ri-database-2-line", title: "15,000+ Verified Buyers", desc: "Access our curated database of HNIs, NRIs, and institutional investors actively seeking land. Not tire-kickers — serious buyers with proof of funds." },
  { icon: "ri-funds-line", title: "Command a Premium Price", desc: "Our clients sell at 15–25% above market average. AI pricing, professional presentation, and competitive buyer dynamics drive your final price up." },
  { icon: "ri-speed-mini-line", title: "45–90 Day Closure", desc: "Average time from listing to registration. Pre-qualified buyers and streamlined due diligence eliminate the usual delays of traditional broking." },
  { icon: "ri-shield-star-line", title: "Complete Legal Support", desc: "Title verification, encumbrance clearance, drafting of sale deed, and registration — handled end-to-end by our in-house legal team. No surprises, no disputes." },
  { icon: "ri-eye-2-line", title: "Transparent Process", desc: "Real-time updates at every stage. You know exactly who viewed your property, what offers came in, and where negotiations stand — no black-box broking." },
  { icon: "ri-percent-line", title: "Success-Based Fee", desc: "You pay only when your property sells. 1–2% success fee. Zero upfront costs. Zero marketing charges. Our interests are fully aligned with yours." },
];

const faqs = [
  { q: "How long does it take to sell my property through CashMyProperty?", a: "Most properties sell within 45–90 days. Our AI pre-qualifies buyers before they ever see your listing, which dramatically shortens the sales cycle. High-demand plots in prime locations have closed in under 30 days. We provide a realistic timeline estimate in your free property audit." },
  { q: "What commission or fees do you charge?", a: "Our success fee is 1–2% of the transaction value, charged only upon successful closure. There are no upfront listing fees, no marketing charges, and no hidden costs. The AI Feasibility Report at ₹4,999 is the only pre-sale investment — and it often helps you command a 15–25% higher price." },
  { q: "Do you list on regular property portals?", a: "We go far beyond portals. Our approach combines AI-powered buyer matching from a verified database of 15,000+ active investors, targeted digital campaigns, institutional buyer outreach, and broker-network syndication. Your property gets exposure to serious buyers — not casual browsers." },
  { q: "What documents do I need to sell my property?", a: "You will need the title deed (sale deed), encumbrance certificate (EC), latest tax receipts, Aadhaar/PAN cards of all owners, approved building/layout plan (if applicable), and khata certificate. Our legal team guides you through the complete documentation checklist specific to your property type." },
];

export default function SellPropertyPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Sell Property — Maximum Price, Faster Closure, Verified Buyers | CashMyProperty.ai",
    description: "Sell your property at 15–25% above market price with CashMyProperty. AI-powered price benchmarking, access to 15,000+ verified buyers, multi-channel marketing, and complete legal support from listing to registration. 45–90 day average closure with success-based pricing.",
    canonicalUrl: "https://cashmyproperty.ai/services/sell-property",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20real%20estate%20agent%20and%20property%20owner%20shaking%20hands%20in%20front%20of%20luxury%20property%20at%20golden%20hour%2C%20warm%20amber%20lighting%2C%20closed%20deal%20celebration%20mood%2C%20architectural%20photography%2C%20clean%20modern%20aesthetic%2C%20India%20luxury%20real%20estate%2C%20high%20detail%2C%20editorial%20composition%2C%20deep%20teal%20and%20gold%20palette&width=1800&height=800&seq=service-sell-property-hero&orientation=landscape"
          alt="Sell Property — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Sell Your</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Property Faster</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              AI-driven price discovery, verified buyer matching, and full legal support. Get the maximum price in the shortest time.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Sell Property</span>
          </nav>
        </div>
      </section>

      {/* What is Sell Property */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What We Do</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Your Property, Maximum Price, Zero Headaches
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Selling land is not the same as selling an apartment. It requires price discovery that accounts for development potential, zoning upside, and buyer profiles — not just recent sales in the neighborhood. Traditional brokers price for speed, not value.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Our AI engine analyzes <strong className="text-foreground-950">50+ data points</strong> — upcoming infrastructure, demand trends, comparable transactions, and buyer appetite — to determine the true market value. Clients consistently achieve <strong className="text-foreground-950">15–25% higher prices</strong> than broker-mediated sales.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                We go beyond listing. We <strong className="text-foreground-950">actively match</strong> your property with verified buyers from our network of 15,000+ HNIs, NRIs, and institutional investors. No open houses, no tire-kickers — just serious, pre-qualified prospects ready to close.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20glass%20office%20meeting%20room%20with%20property%20documents%2C%20architectural%20plans%20spread%20across%20wooden%20table%2C%20warm%20amber%20lighting%2C%20professional%20real%20estate%20negotiation%20setting%2C%20clean%20aesthetic%2C%20India%20corporate%20real%20estate%2C%20high%20detail%20photography%2C%20teal%20and%20gold%20tones&width=800&height=600&seq=sell-property-meeting-01&orientation=landscape"
                alt="Sell Property — Property Valuation Meeting"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">How We Sell Your Property</h2>
            <p className="text-foreground-600">From audit to registration. Five steps to maximum value.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Buyer Categories */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Who Buys Through Us</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Verified Buyer Categories</h2>
            <p className="text-foreground-600">Serious buyers. Pre-qualified. Ready to transact.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: "ri-user-star-line", title: "High Net-Worth Individuals", desc: "Business owners and professionals investing surplus capital in land. Average ticket size ₹2–10 Cr.", color: "bg-primary-500/10 text-primary-700" },
              { icon: "ri-global-line", title: "NRI Buyers", desc: "Non-resident Indians seeking land investment in growing Indian cities. Strong demand for Bangalore, Mysore, and tier-2 cities.", color: "bg-secondary-500/10 text-secondary-700" },
              { icon: "ri-building-2-line", title: "Institutional Investors", desc: "Funds and family offices deploying capital in land aggregation. Seeking 5+ acre parcels in growth corridors.", color: "bg-primary-500/10 text-primary-700" },
              { icon: "ri-home-gear-line", title: "End-Use Buyers", desc: "Families and individuals buying land to build their dream home. Strong preference for plotted developments with clear titles.", color: "bg-secondary-500/10 text-secondary-700" },
              { icon: "ri-store-2-line", title: "Commercial Buyers", desc: "Businesses acquiring land for warehouses, retail, offices, or hospitality projects. Highway and arterial road locations preferred.", color: "bg-primary-500/10 text-primary-700" },
              { icon: "ri-building-line", title: "Developer Buyers", desc: "Real estate developers seeking land parcels for residential or commercial projects. Active across all major Karnataka cities.", color: "bg-secondary-500/10 text-secondary-700" },
            ].map((cat) => (
              <div key={cat.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${cat.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{cat.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">{cat.desc}</p>
                <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full ${cat.color}`}>Active Buyer Pool</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Why Sell Through CashMyProperty</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "15,000+", label: "Verified Buyer Network" },
              { value: "15–25%", label: "Above Market Price" },
              { value: "45–90 Days", label: "Average Sale Timeline" },
              { value: "₹850 Cr+", label: "Property Value Transacted" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Ready to Discover What Your Property is Really Worth?
          </h2>
          <p className="text-foreground-600 mb-8">
            Get your free property audit and see which verified buyers are looking for land like yours right now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get My Property Audit — ₹4,999
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Talk to a Strategist
              <i className="ri-message-3-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}